import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface InboundScreeningCall {
  id: string;
  candidate_id?: string;
  candidate_lookup_query: string;
  elevenlabs_call_id: string;
  call_initiated_at: string;
  status: 'pending' | 'completed' | 'failed';
  transcript?: string;
  analysis_results?: any;
  session_data?: any;
}

interface InboundScreeningStats {
  totalCalls: number;
  completedCalls: number;
  failedCalls: number;
  averageDuration: number;
  recentCalls: InboundScreeningCall[];
}

export const useInboundScreening = () => {
  const [calls, setCalls] = useState<InboundScreeningCall[]>([]);
  const [stats, setStats] = useState<InboundScreeningStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch inbound screening calls
  const fetchInboundCalls = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('voice_screening_sessions')
        .select(`
          id, candidate_id, candidate_lookup_query, elevenlabs_call_id,
          call_initiated_at, transcript, screening_results, completed_at,
          session_status, duration_seconds
        `)
        .eq('is_inbound_call', true)
        .order('call_initiated_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Transform data to match interface
      const transformedCalls: InboundScreeningCall[] = (data || []).map(call => {
        // Determine status based on completed_at and session_status
        let status: 'pending' | 'completed' | 'failed' = 'pending';
        if (call.completed_at) {
          status = call.transcript ? 'completed' : 'failed';
        } else if (call.session_status) {
          status = call.session_status as 'pending' | 'completed' | 'failed';
        }

        return {
          id: call.id,
          candidate_id: call.candidate_id,
          candidate_lookup_query: call.candidate_lookup_query || '',
          elevenlabs_call_id: call.elevenlabs_call_id || '',
          call_initiated_at: call.call_initiated_at || '',
          status,
          transcript: call.transcript,
          analysis_results: call.screening_results,
          session_data: { duration_seconds: call.duration_seconds }
        };
      });

      setCalls(transformedCalls);
      
      // Calculate stats
      const totalCalls = transformedCalls.length;
      const completedCalls = transformedCalls.filter(call => call.status === 'completed').length;
      const failedCalls = transformedCalls.filter(call => call.status === 'failed').length;
      
      const completedCallsWithDuration = transformedCalls.filter(call => 
        call.status === 'completed' && call.session_data?.duration_seconds
      );
      
      const averageDuration = completedCallsWithDuration.length > 0
        ? completedCallsWithDuration.reduce((sum, call) => 
            sum + (call.session_data?.duration_seconds || 0), 0
          ) / completedCallsWithDuration.length
        : 0;

      setStats({
        totalCalls,
        completedCalls,
        failedCalls,
        averageDuration,
        recentCalls: transformedCalls.slice(0, 10)
      });

    } catch (error) {
      console.error('Error fetching inbound calls:', error);
      toast.error('Failed to load inbound screening calls');
    } finally {
      setIsLoading(false);
    }
  };

  // Set up real-time subscription for new calls
  useEffect(() => {
    fetchInboundCalls();

    const channel = supabase
      .channel('inbound_screening_calls')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'voice_screening_sessions',
          filter: 'is_inbound_call=eq.true'
        },
        () => {
          fetchInboundCalls(); // Refresh data when changes occur
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    calls,
    stats,
    isLoading,
    refetch: fetchInboundCalls
  };
};